'use client';

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Link from 'next/link';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tip } from '@/components/ui/tooltip';
import { useT } from '@/components/i18n-provider';
import { getToolIcon } from '@/lib/tools/tool-icons';
import { matchAppBuildSentinel } from '@/lib/llm/app-build-guard';
import {
  getToolLabel,
  formatToolResult,
  getInteractiveResult,
  getTextEditorResult,
  getBashResult,
  getDiscoveryResult,
} from '@/components/tool-renderers';
import {
  // Every `<Markdown>` below passes `suppressLinkPreviews`, deliberately.
  // A bare URL otherwise becomes a `<LinkPreview>`, which frames the platform
  // embed for YouTube, Spotify, Vimeo, SoundCloud, TikTok and X. Tool results
  // are agent output, so the URLs in them are not something a person chose to
  // embed, and this strip renders inside the canvas, where a cross-origin
  // isolated document refuses a COEP-less frame on every engine except
  // Chromium. Suppressed, the URL stays an ordinary link and behaves the same
  // everywhere. The chat bubbles themselves already do this
  // (`chat-view.tsx`); these three were the gap.
  Markdown,
  AttachmentThumb,
  FormatProgressLines,
  formatJSON,
} from '@/components/markdown';
import { useChatHost } from '@/components/chat/chat-host-context';
import { useDelegatedAgent } from '@/lib/chat/resolve-delegated-agent';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToolStripItem = {
  id: string;
  /** 'tool' (default) or 'thinking' — thinking entries render as a brain chip
   *  in the strip and as a muted "Thinking" details panel when selected. */
  kind?: 'tool' | 'thinking';
  name: string;
  args?: string;
  result?: string;
  /** For kind === 'thinking', the raw thought text. */
  text?: string;
  isError?: boolean;
  isStreaming?: boolean;
  progressKey?: string;
  progressSegments?: Array<{ kind: 'append' | 'replace' | 'thinking'; text: string }>;
  /** The call is held for the person's spending approval (`notice: 'approval_wait'`). */
  awaitingApproval?: boolean;
  /** Optional structured metadata returned alongside the tool result.
   *  For NLI/delegate this carries `{ subChatId }` used to render a live
   *  sub-chat preview. */
  data?: Record<string, unknown>;
};

export type ToolStripLevel = 'top' | 'sub';

/**
 * A strip item's label. A call held for the person's approval says so
 * (2026-11-03): the dev browser pass found an image card reading "Generating
 * image" for the whole five-minute wait, then nothing after Approve.
 */
function stripItemLabel(t: ReturnType<typeof useT>, item: Pick<ToolStripItem, 'name' | 'args' | 'result' | 'isStreaming' | 'awaitingApproval'>): string {
  if (item.awaitingApproval && item.isStreaming) return t('tools.awaitingYourApproval');
  return getToolLabel(t, item.name, item.args, item.result, item.isStreaming);
}

// ---------------------------------------------------------------------------
// Shared style tokens — keep paddings/typography uniform across all variants.
// ---------------------------------------------------------------------------

const TITLE_CLS = 'text-xs truncate';
const TITLE_MUTED_CLS = 'text-muted-foreground/70';
const BODY_TEXT_CLS = 'text-xs text-foreground/55 dark:text-foreground/45 break-words whitespace-pre-wrap leading-relaxed';
const SUB_META_CLS = 'text-[11px] text-muted-foreground/45 leading-relaxed';

const COLLAPSED_MAX_H = 120; // px — about 1 title + 5 body lines
const EXPANDED_MAX_H_VH = 65; // vh — ~3/4 chat height
const FADE_PX = 20;
const SCROLL_DELTA_GATE = 16;
const SCROLL_TIME_GATE_MS = 80;
const NEAR_BOTTOM_PX = 24;
const IDLE_AUTO_CLOSE_MS = 4000;

// ---------------------------------------------------------------------------
// ToolCallStrip — horizontal icon row + single shared details panel below.
// ---------------------------------------------------------------------------

/**
 * True for NLI/delegate tool items — the "talked to @agent" rows. Used
 * to give those chips a second side-effect (open the delegated agent
 * widget on the workspace canvas) on top of the default details-toggle.
 */
function isNliToolItem(item: ToolStripItem): boolean {
  if (item.kind === 'thinking') return false;
  return /^(nli|delegate)/.test(item.name.replace(/_tool$/, ''));
}

/**
 * True for the app-building guard's REFUSAL body: the "App building is off"
 * card with the "Turn on app building" button (`AppBuildGuardCard` in
 * `components/tool-renderers.tsx`, guard in `lib/llm/app-build-guard.ts`).
 *
 * WHY THE STRIP HAS TO KNOW. A tool body only exists in the DOM while its chip
 * is the SELECTED one, and the strip selects a chip by itself only while that
 * tool is streaming; it then drops the selection again as soon as the next
 * thing starts arriving (`textStreamingAfter`) or after `IDLE_AUTO_CLOSE_MS`.
 * On a reload nothing streams at all, so nothing is ever selected. Both paths
 * were observed on dev on 2026-09-06: `[data-testid="app-build-off-card"]`
 * matched zero nodes and the card appeared only on a chip click, so what a
 * person actually read was the model's own prose about the refusal ("I cannot
 * create the HTML file for your todo app") — the button, which is the entire
 * user-visible half of the feature, was never on screen. The 2026-09-05
 * `initiallyExpanded` fix could not have reached this: it sets the body's
 * max-height, not whether the panel is mounted.
 *
 * Only the 'off' kind opens itself. The 'redirect' kind is informational and
 * is immediately followed by the real `create_app` chip, whose streaming body
 * is what the person should be watching. Keep this list at one entry: every
 * strip that opens itself is the same as none of them doing it.
 */
function isSelfOpeningGuardBody(item: ToolStripItem): boolean {
  if (item.kind === 'thinking' || item.isError || !item.result) return false;
  return matchAppBuildSentinel(item.result)?.kind === 'off';
}

export interface ToolCallStripProps {
  items: ToolStripItem[];
  /** True when the parent message is currently streaming additional non-tool
   * content (text/file/thinking) after the tools, so we can auto-close on
   * "next thing arrived". */
  textStreamingAfter?: boolean;
  onApprovalAction?: (action: string, data: unknown) => void;
}

export const ToolCallStrip = memo(function ToolCallStrip({
  items,
  textStreamingAfter,
  onApprovalAction,
}: ToolCallStripProps) {
  const host = useChatHost();
  const streamingItem = useMemo(() => items.find((i) => i.isStreaming), [items]);
  const streamingId = streamingItem?.id ?? null;

  const [selectedId, setSelectedId] = useState<string | null>(streamingId);
  const userPickedRef = useRef(false);
  // Track whether *any* tool in this strip has ever streamed, so the auto-close
  // on "text starts after" still fires even if the streaming-finished tick and
  // the text-arrival tick are separate renders.
  const hasEverStreamedRef = useRef(!!streamingId);

  useEffect(() => {
    if (streamingId) hasEverStreamedRef.current = true;
  }, [streamingId]);

  // The one body in the strip that shows itself: the app-building guard's
  // refusal card (see `isSelfOpeningGuardBody`). Recomputed from the items on
  // every render because the result arrives LATE — the strip mounts while the
  // tool is still streaming with an empty `result`.
  const selfOpeningId = useMemo(
    () => items.find(isSelfOpeningGuardBody)?.id ?? null,
    [items],
  );

  // Ids this strip has already opened on its own. The set is what separates
  // "the sentinel ARRIVED" (open the body) from "the sentinel is STILL THERE
  // on the next render" (leave the person's choice alone). A plain effect on
  // the flag would spring the panel back open on every later streaming update,
  // so a deliberate collapse would never stick.
  const autoOpenedRef = useRef<Set<string>>(new Set());
  // While set, the auto-open/auto-close heuristics keep their hands off the
  // selection: a card a person is meant to ACT on must not vanish on a timer
  // or because the model started talking after it. Cleared the moment they
  // touch any chip, because from then on the selection is theirs.
  const pinnedIdRef = useRef<string | null>(null);

  // Auto-open the streaming item (unless user picked, or a guard card is up).
  useEffect(() => {
    if (userPickedRef.current) return;
    if (pinnedIdRef.current) return;
    if (streamingId) {
      setSelectedId(streamingId);
    }
  }, [streamingId]);

  // Auto-close behavior, in priority order:
  //   1. A pinned (self-opened) body is up → keep open until the person acts.
  //   2. Streaming still in progress → keep open.
  //   3. Next content (text/file) starts arriving → close immediately.
  //   4. Otherwise, after a short idle window, close so the bubble settles.
  useEffect(() => {
    if (userPickedRef.current) return;
    if (pinnedIdRef.current) return;
    if (streamingId) return;
    if (!hasEverStreamedRef.current) return;

    if (textStreamingAfter) {
      setSelectedId(null);
      return;
    }
    // Re-check the pin when the timer FIRES rather than only when it is armed:
    // the guard's result can land in the same commit that arms this, and the
    // effect that pins it is declared below, so it runs after this one.
    const handle = setTimeout(() => {
      if (pinnedIdRef.current) return;
      setSelectedId(null);
    }, IDLE_AUTO_CLOSE_MS);
    return () => clearTimeout(handle);
  }, [streamingId, textStreamingAfter]);

  // Open the guard card once, on the render its result first appears. Declared
  // last on purpose: within a single commit it runs after the auto-close above,
  // so "result arrived while the model was already talking" still ends open.
  useEffect(() => {
    if (!selfOpeningId) return;
    if (autoOpenedRef.current.has(selfOpeningId)) return;
    autoOpenedRef.current.add(selfOpeningId);
    pinnedIdRef.current = selfOpeningId;
    setSelectedId(selfOpeningId);
  }, [selfOpeningId]);

  const handleChipClick = useCallback((id: string) => {
    userPickedRef.current = true;
    // The selection is the person's from here. Drop the pin so a card they
    // deliberately closed stays closed.
    pinnedIdRef.current = null;
    setSelectedId((prev) => (prev === id ? null : id));
  }, []);

  /**
   * Side-effect for NLI/delegate chips: open the delegated agent
   * widget to the right of the host on the workspace canvas, bound
   * to the recorded sub-chat. Reuses the same handler the historical
   * "replay" affordance went through, so spawn / reuse / replace
   * logic in `useDelegationVisualization` stays the single source
   * of truth.
   *
   * Standalone chat views (no workspace host) have no
   * `onReplayDelegation`; the callback is a no-op there and the
   * chip behaves like any other tool chip (just toggles details).
   */
  const handleAgentChipClick = useCallback(
    (item: ToolStripItem) => {
      const parsedData = item.data as { subChatId?: string } | undefined;
      if (!host.onReplayDelegation) {
        // Surface the no-op so an unexpected "click does nothing" in
        // a workspace embed is observable. Standalone chat surfaces
        // (no workspace host) hit this branch by design.
        // eslint-disable-next-line no-console
        console.debug(
          '[tool-call-strip] delegate chip clicked outside workspace host — no-op',
          { callId: item.id, hasSubChatId: !!parsedData?.subChatId },
        );
        return;
      }
      // eslint-disable-next-line no-console
      console.debug('[tool-call-strip] delegate chip → onReplayDelegation', {
        callId: item.id,
        toolName: item.name,
        hasSubChatId: !!parsedData?.subChatId,
        hasArgs: !!item.args,
        hasChatId: !!host.chatId,
      });
      void host.onReplayDelegation({
        callId: item.id,
        args: item.args,
        result: item.result,
        data: parsedData?.subChatId
          ? { subChatId: parsedData.subChatId }
          : undefined,
        chatId: host.chatId,
      });
    },
    [host],
  );

  const selected = selectedId ? items.find((i) => i.id === selectedId) : undefined;

  return (
    <div className="min-w-0 max-w-full">
      <ChipRow
        items={items}
        selectedId={selectedId}
        onChipClick={handleChipClick}
        onAgentChipClick={handleAgentChipClick}
      />
      {selected && (
        <div
          key={selected.id}
          className="mt-0.5 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-0.5 motion-safe:duration-200"
        >
          <ToolCallDetails
            item={selected}
            level="top"
            onApprovalAction={onApprovalAction}
          />
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// ChipRow — bare lucide icons, no chip background.
// ---------------------------------------------------------------------------

function ChipRow({
  items,
  selectedId,
  onChipClick,
  onAgentChipClick,
}: {
  items: ToolStripItem[];
  selectedId: string | null;
  onChipClick: (id: string) => void;
  /** Fires for NLI/delegate items in addition to `onChipClick`. The
   *  workspace consumer uses it to spawn the delegated agent widget
   *  on the canvas. Outside a workspace host this is undefined and
   *  the chip behaves like every other tool chip. */
  onAgentChipClick?: (item: ToolStripItem) => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2 py-0.5 min-w-0">
      {items.map((item) => {
        const isThinking = item.kind === 'thinking';
        const Icon = isThinking ? getToolIcon('thinking') : getToolIcon(item.name);
        const isSelected = item.id === selectedId;
        const isAgentChip = isNliToolItem(item);
        const title = isThinking
          ? t('tools.thinking')
          : stripItemLabel(t, item);
        return (
          <Tip key={item.id} label={title} side="top">
            <button
              type="button"
              onClick={() => {
                onChipClick(item.id);
                if (isAgentChip) onAgentChipClick?.(item);
              }}
              aria-label={title}
              aria-pressed={isSelected}
              className={cn(
                'shrink-0 inline-flex items-center justify-center transition-colors duration-150 focus:outline-none focus-visible:text-foreground motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-90 motion-safe:duration-300',
                item.isError && 'text-red-500/80 hover:text-red-500',
                !item.isError && (
                  item.isStreaming
                    ? 'text-purple-500 dark:text-purple-400 motion-safe:animate-tool-active'
                    : isSelected
                    ? 'text-foreground'
                    : 'text-muted-foreground/60 hover:text-foreground'
                ),
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          </Tip>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToolCallDetails — sticky title + scroll body + fades + expand toggle.
// Reused for both top-level (purple dot) and nested sub-entries (gray dot).
// ---------------------------------------------------------------------------

export function ToolCallDetails({
  item,
  level,
  onApprovalAction,
}: {
  item: ToolStripItem;
  level: ToolStripLevel;
  onApprovalAction?: (action: string, data: unknown) => void;
}) {
  const t = useT();
  const { kind, name, args, result, text, isError, isStreaming, progressKey, progressSegments, data } = item;

  // ---- thinking variant: short-circuit with a muted body ----
  if (kind === 'thinking') {
    const trimmed = (text ?? '').trim();
    return (
      <DetailsShell
        level={level}
        title={t('tools.thinking')}
        isStreaming={!!isStreaming}
        isError={false}
        streamingTrigger={trimmed.length}
      >
        <div className={cn('pl-3', SUB_META_CLS, 'whitespace-pre-wrap break-words italic min-w-0')}>
          {trimmed || (isStreaming ? null : t('tools.noResult'))}
        </div>
      </DetailsShell>
    );
  }

  const isNliTool = /^(nli|delegate)/.test(name.replace(/_tool$/, ''));
  const isMediaGen = /^generate_(image|video|audio|3d)/.test(name.replace(/_tool$/, ''));

  const title = stripItemLabel(t, item);

  // ---- header content (NLI gets a purple @agent link inside the title) ----
  // The canvas-spawn action lives on the *chip itself* (see
  // `ChipRow` / `handleAgentChipClick` in `ToolCallStrip`), so this
  // title row no longer carries a redundant explicit "replay" icon.
  // The `@handle` link below stays a plain profile link.
  //
  // For NLI/delegate rows we try the args-scrape first (the live
  // event path always has it), then fall back to resolving the
  // delegated agent via the sub-chat metadata — historical rows
  // persisted by MCP/external callers often have args that omit
  // every flavor of `agent*` key, but they always have `subChatId`
  // in `data`. The hook upgrades the header asynchronously, so the
  // initial paint stays "Talked to agent" and re-renders to "Talked
  // to @flux_schnell" once the lookup lands.
  const argsAgent = (() => {
    if (!isNliTool) return undefined;
    try {
      const p = args ? JSON.parse(args) : {};
      return (p.agent || p.agent_url || p.agent_identifier) as string | undefined;
    } catch {
      return undefined;
    }
  })();
  const subChatIdFromData = (data as { subChatId?: string } | undefined)?.subChatId;
  const resolved = useDelegatedAgent(
    isNliTool && !argsAgent ? subChatIdFromData : undefined,
  );
  const nliHandle = (() => {
    if (!isNliTool) return undefined;
    if (argsAgent) return argsAgent.replace(/^@/, '');
    if (resolved?.username) return resolved.username;
    return undefined;
  })();
  const titleNode: ReactNode = (() => {
    if (isNliTool && nliHandle) {
      return (
        <span className="inline-flex items-center gap-1 min-w-0">
          <span className="truncate">
            {isStreaming ? t('tools.talkingTo') : t('tools.talkedTo')}{' '}
            <Link
              href={`/u/${nliHandle}`}
              className="hover:text-purple-400 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{nliHandle}
            </Link>
          </span>
        </span>
      );
    }
    return title;
  })();

  // ---- body content ----
  const interactiveUI = (result || data || args) ? getInteractiveResult(t, name, result ?? '', onApprovalAction, data, args, isStreaming, isError) : null;
  const textEditorUI = !interactiveUI && !isError ? getTextEditorResult(t, name, args, result) : null;
  const bashUI = !interactiveUI && !textEditorUI && !isError ? getBashResult(t, name, args, result) : null;
  // The app-building guard's refusal is not a routine tool result: it is an
  // ACTIONABLE card with a button, and it is the whole user-visible half of the
  // feature. Two separate things were hiding it (both seen on dev, 2026-09-06):
  // the panel was not SELECTED (fixed in `ToolCallStrip` above), and the body
  // is clipped to COLLAPSED_MAX_H, which the card overflows. This flag handles
  // the second. Matching on the sentinel rather than the tool name keeps it to
  // the guard's own results; `matchAppBuildSentinel` is deliberately
  // dependency-free so a client component may import it.
  const isActionableGuardCard = isSelfOpeningGuardBody(item);
  const discoveryUI = !interactiveUI && !textEditorUI && !bashUI && result && !isError ? getDiscoveryResult(t, name, result) : null;
  const formattedResult = result && !isError && !interactiveUI && !textEditorUI && !bashUI && !discoveryUI ? formatToolResult(t, name, result) : null;

  const nliRequest = isNliTool ? (() => {
    try { const p = args ? JSON.parse(args) : {}; return (p.message as string) || undefined; } catch { return undefined; }
  })() : undefined;
  const nliAttachments = isNliTool ? (() => {
    try {
      const p = args ? JSON.parse(args) : {};
      if (!Array.isArray(p.attachments)) return undefined;
      return (p.attachments as string[]).map((a) => {
        const m = a.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
        return m ? m[1] : a;
      });
    } catch { return undefined; }
  })() : undefined;

  const mediaGenParams = isMediaGen ? (() => {
    try {
      const p = args ? JSON.parse(args) : {};
      const lines: string[] = [];
      if (p.prompt) lines.push(`**Prompt:** ${p.prompt}`);
      if (p.image_url) lines.push(`**Input image:** ${p.image_url}`);
      if (p.duration) lines.push(`**Duration:** ${p.duration}s`);
      if (p.aspect_ratio) lines.push(`**Aspect ratio:** ${p.aspect_ratio}`);
      if (p.image_size) lines.push(`**Size:** ${p.image_size}`);
      if (p.num_images && p.num_images > 1) lines.push(`**Count:** ${p.num_images}`);
      if (p.seed !== undefined) lines.push(`**Seed:** ${p.seed}`);
      return lines.length > 0 ? lines.join('\n\n') : null;
    } catch { return null; }
  })() : null;

  const body: ReactNode = (() => {
    // NLI/delegate: request → attachments → segments (chronological for streaming)
    if (isNliTool) {
      const segs = progressSegments ?? [];
      const hasSegs = segs.length > 0;
      const lastIdx = segs.length - 1;
      return (
        <div className="pl-3 space-y-1.5 min-w-0">
          {nliAttachments && nliAttachments.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {nliAttachments.map((id) => (
                <AttachmentThumb key={id} id={id} />
              ))}
            </div>
          )}
          {nliRequest && (
            <div className={cn(SUB_META_CLS, 'italic whitespace-pre-wrap break-words max-h-20 overflow-y-auto scrollbar-hover-only')}>
              {nliRequest}
            </div>
          )}
          {hasSegs ? (
            <div className="space-y-1.5 min-w-0">
              {segs.map((seg, i) => {
                const isLast = i === lastIdx;
                if (seg.kind === 'thinking') {
                  return <NestedThinking key={`seg-${i}`} text={seg.text} isStreaming={!!isStreaming && isLast} />;
                }
                return (
                  <div
                    key={`seg-${i}`}
                    className={cn(BODY_TEXT_CLS, 'min-w-0')}
                  >
                    <FormatProgressLines text={seg.text} />
                  </div>
                );
              })}
            </div>
          ) : interactiveUI ? (
            <div className="min-w-0">{interactiveUI}</div>
          ) : discoveryUI ? (
            <div className={cn(BODY_TEXT_CLS, 'min-w-0')}>{discoveryUI}</div>
          ) : formattedResult ? (
            <div className={cn(BODY_TEXT_CLS, 'prose prose-sm dark:prose-invert max-w-none prose-p:text-muted-foreground/65 min-w-0')}>
              <Markdown suppressLinkPreviews>{formattedResult}</Markdown>
            </div>
          ) : result ? (
            (() => {
              const trimmed = result.trim();
              if (!trimmed) return null;
              if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                return (
                  <pre className={cn(BODY_TEXT_CLS, 'font-mono break-all max-w-full', isError && 'text-red-500/80')}>
                    {formatJSON(result)}
                  </pre>
                );
              }
              return (
                <div className={cn(BODY_TEXT_CLS, 'min-w-0')}>
                  <FormatProgressLines text={result} />
                </div>
              );
            })()
          ) : isStreaming ? (
            <div className={cn('text-xs text-muted-foreground/45 italic')}>
              {t('tools.waitingForResponse')}
            </div>
          ) : (
            <div className={cn(BODY_TEXT_CLS)}>{t('tools.noResponse')}</div>
          )}
        </div>
      );
    }

    // MediaGen: gen params + streaming progress text
    if (isMediaGen) {
      return (
        <div className="pl-3 space-y-1.5 min-w-0">
          {mediaGenParams && (
            // One paragraph per parameter; prose's default paragraph margin
            // left a gap the size of a line between "Prompt" and "Size".
            <div className={cn(BODY_TEXT_CLS, 'prose prose-sm dark:prose-invert max-w-none prose-p:text-muted-foreground/65 prose-p:my-0.5 min-w-0')}>
              <Markdown suppressLinkPreviews>{mediaGenParams}</Markdown>
            </div>
          )}
          {isStreaming && result && (
            <div
              key={progressKey ?? result}
              className="text-xs text-muted-foreground/60 italic motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-0.5 motion-safe:duration-300"
            >
              {result}
            </div>
          )}
        </div>
      );
    }

    // Default tool body: interactive → text-editor → discovery → markdown → JSON
    return (
      <div className="pl-3 min-w-0">
        {interactiveUI ? (
          <div className="min-w-0">{interactiveUI}</div>
        ) : textEditorUI ? (
          <div className="min-w-0">{textEditorUI}</div>
        ) : bashUI ? (
          <div className="min-w-0">{bashUI}</div>
        ) : discoveryUI ? (
          <div className={cn(BODY_TEXT_CLS, 'min-w-0')}>{discoveryUI}</div>
        ) : formattedResult ? (
          <div className={cn(BODY_TEXT_CLS, 'prose prose-sm dark:prose-invert max-w-none prose-p:text-muted-foreground/65 min-w-0')}>
            <Markdown suppressLinkPreviews>{formattedResult}</Markdown>
          </div>
        ) : result ? (
          <pre className={cn(BODY_TEXT_CLS, 'font-mono break-all max-w-full', isError && 'text-red-500/80')}>
            {formatJSON(result)}
          </pre>
        ) : isStreaming ? null : (
          <div className={BODY_TEXT_CLS}>{t('tools.noResult')}</div>
        )}
      </div>
    );
  })();

  return (
    <DetailsShell
      level={level}
      title={titleNode}
      isStreaming={!!isStreaming}
      isError={!!isError}
      streamingTrigger={result?.length ?? 0}
      initiallyExpanded={isActionableGuardCard}
    >
      {body}
    </DetailsShell>
  );
}

// ---------------------------------------------------------------------------
// DetailsShell — sticky title + scroll body + fades + expand toggle.
// Encapsulates layout & motion so all variants share identical chrome.
// ---------------------------------------------------------------------------

function DetailsShell({
  level,
  title,
  isStreaming,
  isError,
  streamingTrigger,
  initiallyExpanded = false,
  children,
}: {
  level: ToolStripLevel;
  title: ReactNode;
  isStreaming: boolean;
  isError: boolean;
  streamingTrigger: number;
  /** Show the body at full height instead of clipped to COLLAPSED_MAX_H. For
   *  bodies a person must ACT on rather than merely inspect, so far only the
   *  app-building guard card. Keep this rare: every strip opening itself is
   *  the same as none of them doing it. May flip false→true after mount, since
   *  the tool result that decides it streams in. */
  initiallyExpanded?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  const [bodyExpanded, setBodyExpanded] = useState(initiallyExpanded);
  // `useState` seeds ONCE. This panel normally mounts while its tool is still
  // streaming and `result` is empty, so `initiallyExpanded` is false at mount
  // and the guard card stayed clipped even after its result landed (dev,
  // 2026-09-06). Expand on the false→true TRANSITION instead, exactly once:
  // "the flag became true" opens the body, while "the flag is true again" on a
  // later re-render must never undo a collapse the person chose.
  const autoExpandedRef = useRef(initiallyExpanded);
  useEffect(() => {
    if (!initiallyExpanded || autoExpandedRef.current) return;
    autoExpandedRef.current = true;
    setBodyExpanded(true);
  }, [initiallyExpanded]);
  const [showTopFade, setShowTopFade] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const lastScrollAtRef = useRef(0);
  const lastScrollHeightRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const updateFades = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setShowTopFade(scrollTop > 4);
    setOverflowing(scrollHeight - clientHeight > 4);
  }, []);

  // Track user scroll to decide whether to stick to bottom
  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
    userScrolledUpRef.current = distFromBottom > NEAR_BOTTOM_PX;
    updateFades();
  }, [updateFades]);

  // Throttled, perception-aware stick-to-bottom on streaming updates
  useEffect(() => {
    if (!isStreaming) return;
    const el = bodyRef.current;
    if (!el) return;
    if (userScrolledUpRef.current) {
      updateFades();
      return;
    }
    const now = performance.now();
    const grew = el.scrollHeight - lastScrollHeightRef.current;
    if (grew < SCROLL_DELTA_GATE && lastScrollHeightRef.current !== 0) {
      updateFades();
      return;
    }
    if (now - lastScrollAtRef.current < SCROLL_TIME_GATE_MS) {
      // Coalesce: schedule one rAF tick later
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const node = bodyRef.current;
        if (!node) return;
        if (userScrolledUpRef.current) return;
        node.scrollTop = node.scrollHeight;
        lastScrollAtRef.current = performance.now();
        lastScrollHeightRef.current = node.scrollHeight;
        updateFades();
      });
      return;
    }
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const node = bodyRef.current;
      if (!node) return;
      if (userScrolledUpRef.current) return;
      node.scrollTop = node.scrollHeight;
      lastScrollAtRef.current = performance.now();
      lastScrollHeightRef.current = node.scrollHeight;
      updateFades();
    });
  }, [streamingTrigger, isStreaming, updateFades]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Initial fade calc + recompute on body content / expand changes
  useLayoutEffect(() => {
    updateFades();
  }, [updateFades, bodyExpanded, children]);

  // Observe size changes (content streaming in, expand toggle, etc.)
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateFades());
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [updateFades]);

  const dotCls = level === 'top' ? 'bg-purple-400' : 'bg-muted-foreground/40';
  const titleCls = isStreaming
    ? 'tool-title-shimmer'
    : isError
    ? 'text-red-500/80'
    : TITLE_MUTED_CLS;

  const maxHStyle: React.CSSProperties = bodyExpanded
    ? { maxHeight: `${EXPANDED_MAX_H_VH}vh` }
    : { maxHeight: `${COLLAPSED_MAX_H}px` };

  return (
    <div className="relative min-w-0 max-w-full">
      <div
        ref={bodyRef}
        onScroll={handleScroll}
        style={maxHStyle}
        className="scrollbar-hover-only overflow-y-auto overflow-x-hidden scroll-smooth min-w-0 max-w-full transition-[max-height] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
      >
        {/* sticky title row — sticks inside this scroll container only */}
        <div className="sticky top-0 z-20 bg-muted/95 backdrop-blur-[2px] flex items-center gap-1.5 py-0.5 min-w-0">
          <span
            className={cn(
              'h-1.5 w-1.5 rounded-full shrink-0',
              dotCls,
              isStreaming && 'motion-safe:animate-pulse',
            )}
          />
          <span className={cn(TITLE_CLS, titleCls, 'min-w-0')}>{title}</span>
        </div>
        <div className="mt-0.5">{children}</div>
        {overflowing && <div style={{ height: FADE_PX + 4 }} aria-hidden />}
      </div>
      {showTopFade && (
        <div
          className="pointer-events-none absolute inset-x-0 z-10 bg-gradient-to-b from-muted to-transparent motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
          style={{ height: FADE_PX, top: 18 /* below sticky title */ }}
          aria-hidden
        />
      )}
      {overflowing && (
        <Tip label={bodyExpanded ? t('tools.collapse') : t('tools.expand')} side="left">
          <button
            type="button"
            onClick={() => setBodyExpanded((p) => !p)}
            aria-label={bodyExpanded ? t('tools.collapse') : t('tools.expand')}
            className="absolute bottom-0.5 right-0.5 z-20 inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted/40 transition-colors duration-150"
          >
            {bodyExpanded ? (
              <ChevronsDownUp className="h-3 w-3" />
            ) : (
              <ChevronsUpDown className="h-3 w-3" />
            )}
          </button>
        </Tip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NestedThinking — sub-level "thinking" entry inside a delegate body.
// Reuses the same dot+title pattern with a gray dot, no extra chrome.
// ---------------------------------------------------------------------------

function NestedThinking({ text, isStreaming }: { text: string; isStreaming: boolean }) {
  const t = useT();
  const trimmed = text.trim();
  if (!trimmed) return null;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 py-0.5 min-w-0">
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0',
            isStreaming && 'motion-safe:animate-pulse',
          )}
        />
        <span className={cn(TITLE_CLS, isStreaming ? 'tool-title-shimmer' : TITLE_MUTED_CLS)}>
          {t('tools.thinking')}
        </span>
      </div>
      <div className={cn('pl-3', SUB_META_CLS, 'whitespace-pre-wrap break-words italic')}>
        {trimmed}
      </div>
    </div>
  );
}
